import { useEffect, useMemo } from "react";
import { ArrowUpRight } from "lucide-react";
import { useWebsitesStore } from "../../store/websitesStore";
import { builderUrl } from "../../utils/builderUrl";
import type { Order } from "../../pages/SalesPage/SalesPageInterface";

interface OrderFunnelLinkProps {
  order: Pick<Order, "website_id" | "website_name" | "funnel_name" | "configurations">;
  variant?: "cell" | "card";
}

const getKnownOrderFunnelName = (order: OrderFunnelLinkProps["order"]) =>
  order.configurations?.name_funnel?.trim() ||
  order.funnel_name?.trim() ||
  order.website_name?.trim() ||
  "";

const getWebsiteDisplayName = (
  websiteId: string,
  websites: ReturnType<typeof useWebsitesStore.getState>["websites"]
) => {
  const website = websites.find((item) => item.websiteId === websiteId);
  return (
    website?.configurations?.name_funnel?.trim() ||
    website?.name?.trim() ||
    website?.productName?.trim() ||
    ""
  );
};

const getBuilderUrl = (websiteId: string) => builderUrl(websiteId) || "";

const getShortFunnelLabel = (websiteId: string) =>
  websiteId.length > 8 ? `Embudo ${websiteId.slice(0, 8)}` : "Embudo";

export const OrderFunnelLink = ({ order, variant = "cell" }: OrderFunnelLinkProps) => {
  const websiteId = String(order.website_id || "").trim();
  const websites = useWebsitesStore((state) => state.websites);
  const isLoading = useWebsitesStore((state) => state.isLoading);
  const initialize = useWebsitesStore((state) => state.initialize);

  useEffect(() => {
    if (!websiteId || websites.length > 0 || isLoading) return;
    void initialize();
  }, [initialize, isLoading, websiteId, websites.length]);

  const funnelName = useMemo(() => {
    if (!websiteId) return "";
    return (
      getKnownOrderFunnelName(order) ||
      getWebsiteDisplayName(websiteId, websites) ||
      getShortFunnelLabel(websiteId)
    );
  }, [order, websiteId, websites]);

  if (!websiteId) {
    return (
      <span className="inline-flex text-sm text-gray-400">
        -
      </span>
    );
  }

  const builderUrl = getBuilderUrl(websiteId);

  if (variant === "card") {
    return (
      <div className="rounded-lg bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Embudo</p>
            <h2 className="mt-1 truncate text-lg font-semibold text-gray-900">{funnelName}</h2>
            <p className="mt-1 truncate text-xs text-gray-400">{websiteId}</p>
          </div>
          {builderUrl ? (
            <a
              href={builderUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition hover:border-[#4368E5]/30 hover:bg-[#4368E5]/10 hover:text-[#4368E5]"
              aria-label="Abrir editor del embudo"
              title="Abrir editor"
            >
              <ArrowUpRight className="h-4 w-4" />
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  if (!builderUrl) {
    return <span className="block max-w-[130px] truncate text-sm text-gray-600" title={funnelName}>{funnelName}</span>;
  }

  return (
    <a
      href={builderUrl}
      target="_blank"
      rel="noreferrer"
      className="group inline-flex max-w-[130px] min-w-0 items-center gap-1.5 text-xs font-medium text-gray-600 transition hover:text-[#4368E5]"
      title={funnelName}
    >
      <span className="min-w-0 truncate">{funnelName}</span>
      <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-gray-300 transition group-hover:text-[#4368E5]" />
    </a>
  );
};
